import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { BarGraph, Grid } from 'components/atoms'
import { Accordion, TabBar } from 'components/molecules'
import { TextContext } from 'hocs/TextContextProvider/TextContextProvider'
import * as Styled from './PatientsExperienceQuestionnaire.styles'
import { useMobileSwitch } from 'hooks'
import DOMPurify from 'dompurify'

/**
 * functional container for all detail views
 * @returns {*}
 * @constructor
 */
const PatientsExperienceQuestionnaire = (props) => {
  const { defaultData, departmentsData, additionalInfo, noValueText } = props

  const [showAverageValues, setShowAverageValues] = useState(false)
  const [showDepartmentTabs, setShowDepartmentTabs] = useState(false)
  const [selectedData, setSelectedData] = useState(defaultData)
  const textContext = useContext(TextContext)
  const isMobile = useMobileSwitch()
  const showNoValueContext = selectedData.overall.percentValue === 0

  // show / hide average values
  const handleToggleAverageClick = () => {
    setShowAverageValues(!showAverageValues)
  }

  // show / hide tab bar
  const handleToggleDepartmentsClick = () => {
    // on hide departments => set selected data to default
    if (showDepartmentTabs) {
      setSelectedData(defaultData)
    }

    // if mobile -> preselect item with max treatment cases
    if (isMobile && !showDepartmentTabs) {
      const departmentWithMaxTreatments = departmentsData.reduce((max, item) =>
        max.treatments.cases > item.treatments.cases ? max : item
      )
      setSelectedData(departmentWithMaxTreatments)
    }

    setShowDepartmentTabs(!showDepartmentTabs)
  }

  // select department data
  const handleTabSelect = (id) => {
    // get department data by id
    const result = departmentsData.filter(({ hospitalId }) => hospitalId === id)

    if (result.length > 0) {
      setSelectedData(result[0])
    }
  }

  /**
   * primary bar graphs (or top level)
   * @param allocationData
   * @param label
   */
  const getAllocatedData = (allocationData, label = '') => {
    if (!allocationData) return

    return (
      <>
        {label !== '' && <p>{label}</p>}

        {/* if itemData has allocation return accordion otherwise return single peq */}
        {Object.keys(allocationData).map((key, index) => {
          const itemData = allocationData[key]

          return (
            <div key={ index } className={ 'ml' }>
              {itemData.allocation
                ? getAccordionPEQ(itemData, key, index)
                : getSinglePEQ(itemData, key, index)}
            </div>
          )
        })}
      </>
    )
  }

  /**
   * get nested bar graphs (second level)
   * @param peqData
   * @param label
   * @param key
   */
  const getAccordionPEQ = (peqData, label = '', key = null) => {
    if (!peqData) return
    const { percentValue, averagePercentValue, allocation } = peqData

    return (
      <Accordion
        key={ key }
        label={ label }
        ratingValue={ percentValue }
        ratingAverage={ averagePercentValue }
        showRatingAverage={ showAverageValues }
        type={ 'RATING' }>
        <>
          <div className={ 'separator' } />
          {getAllocatedData(allocation)}
          <div className={ 'separator' } />
        </>
      </Accordion>
    )
  }

  /**
   * top level bar graph which has no nested graphs
   * @param peqData
   * @param label
   * @param key
   */
  const getSinglePEQ = (peqData, label = '', key = null) => {
    if (!peqData) return
    const { percentValue, averagePercentValue } = peqData

    return (
      <Styled.SinglePEQ key={ key } ratingAverage={ averagePercentValue }>
        <span className={ 'single-peq-label' }>
          {label}
          {averagePercentValue && showAverageValues && (
            <span className='average-value'>
              {' '}
              (&empty; {averagePercentValue}%)
            </span>
          )}
        </span>
        <BarGraph
          className={ 'peq-bar-graph' }
          percent={ percentValue }
          showAverage={ showAverageValues }
          average={ averagePercentValue }
        />
      </Styled.SinglePEQ>
    )
  }

  return (
    <Styled.PEQComponent>
      {/* bar graphs */}
      <Grid container>
        <Grid item xs={ 12 } md={ 4 }>
          {departmentsData && departmentsData.length > 0 && (
            <>
              <p
                className={ 'toggle-link' }
                onClick={ handleToggleDepartmentsClick }>
                {showDepartmentTabs
                  ? textContext['peq.hospitalValues.show']
                  : textContext['peq.departmentValues.show']}
              </p>
              {showDepartmentTabs && (
                <TabBar
                  data={ departmentsData }
                  onTabSelect={ handleTabSelect }
                  selectedId={ selectedData.hospitalId }
                />
              )}
            </>
          )}
        </Grid>
        <Grid item xs={ 12 } md={ 8 }>
          {selectedData && (
            <>
              {!showNoValueContext ? (
                <div>
                  {/* overall ranking */}
                  {getSinglePEQ(
                    selectedData.overall,
                    textContext['peq.label.overall']
                  )}

                  <div className={ 'separator' } />

                  {/* allocated values */}
                  <div className={ 'allocated-peq' }>
                    {getAllocatedData(
                      selectedData.allocation,
                      textContext['peq.label.contented']
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  {noValueText ? (
                    <span>{noValueText}</span>
                  ) : (
                    <span>{textContext['peq.noValue.text']}</span>
                  )}
                </div>
              )}

              {/* peq footer content */}
              <div className={ 'peq-footer-container' }>
                {/* average toggling */}
                {!showNoValueContext && (
                  <span
                    className={ 'toggle-link' }
                    onClick={ handleToggleAverageClick }>
                    {showAverageValues
                      ? textContext['peq.average.hide']
                      : textContext['peq.average.show']}
                  </span>
                )}

                {/* peq hint */}
                {selectedData.overall?.info?.cases &&
                  selectedData.overall?.info?.timeSpan && (
                    <span className={ 'peq-hint' }>
                      {textContext['peq.label.treatmentsCount']}{' '}
                      {selectedData.overall.info.cases} (
                      {selectedData.overall.info.timeSpan})
                    </span>
                  )}
              </div>

              {additionalInfo && !showNoValueContext && (
                <div className={ 'additional-info-container' }>
                  <Accordion
                    label={ textContext['peq.footer.info.label'] }
                    type={ 'UNDERLINED' }>
                    <p
                      className={ 'additional-info-copy' }
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(additionalInfo),
                      }}
                    />
                  </Accordion>
                </div>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Styled.PEQComponent>
  )
}

PatientsExperienceQuestionnaire.defaultProps = {
  noValueText: false,
}

PatientsExperienceQuestionnaire.propTypes = {
  defaultData: PropTypes.shape({
    hospitalId: PropTypes.string,
    name: PropTypes.string,
    treatments: PropTypes.shape({
      cases: PropTypes.number,
    }),
    overall: PropTypes.shape({
      label: PropTypes.string,
      percentValue: PropTypes.number,
      averagePercentValue: PropTypes.number,
      info: PropTypes.shape({
        hint: PropTypes.string,
        title: PropTypes.string,
        copy: PropTypes.string,
      }),
    }),
    allocation: PropTypes.shape({
      title: PropTypes.string,
      values: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          percentValue: PropTypes.number,
          averagePercentValue: PropTypes.number,
        })
      ),
    }),
  }),
  departmentsData: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.arrayOf(
      PropTypes.shape({
        allocation: PropTypes.objectOf(
          PropTypes.shape({
            averagePercentValue: PropTypes.number.isRequired,
            percentValue: PropTypes.number.isRequired,
          })
        ),
        hospitalId: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        overall: PropTypes.shape({
          averagePercentValue: PropTypes.number.isRequired,
          info: PropTypes.shape({
            cases: PropTypes.number.isRequired,
            timeSpan: PropTypes.string.isRequired,
          }),
          percentValue: PropTypes.number.isRequired,
        }),
        treatments: PropTypes.shape({
          cases: PropTypes.number.isRequired,
        }).isRequired,
      })
    ),
  ]),
  additionalInfo: PropTypes.string,
  noValueText: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
}

export default PatientsExperienceQuestionnaire
